import { useState, useEffect } from 'react';
import { supabase } from './supabase';
import {
  Warehouse, Building2, Truck, MapPin, Package, AlertTriangle,
  ArrowRight, Eye, Loader, DollarSign, Star
} from 'lucide-react';
import { useTheme } from './ThemeContext';

const locationTypeIcons = {
  warehouse: Warehouse,
  office: Building2,
  truck: Truck
};

const locationTypeColors = {
  warehouse: '#3498db',
  office: '#9b59b6',
  truck: '#27ae60'
};

export default function VisualLayoutView({
  companyId,
  onViewLocation,
  onTransfer
}) {
  const { colors } = useTheme();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [stockSummaries, setStockSummaries] = useState({});

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (companyId) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [companyId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load locations
      const { data: locationData } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('location_type')
        .order('name');

      setLocations(locationData || []);

      // Load stock summaries
      if (locationData && locationData.length > 0) {
        const { data: stockData } = await supabase
          .from('inventory_stock')
          .select(`
            location_id,
            quantity_on_hand,
            inventory_items!inner(reorder_point, unit_cost)
          `)
          .eq('company_id', companyId);

        // Aggregate by location
        const summaries = {};
        (stockData || []).forEach(stock => {
          if (!summaries[stock.location_id]) {
            summaries[stock.location_id] = {
              itemCount: 0,
              totalUnits: 0,
              totalValue: 0,
              lowStockCount: 0
            };
          }
          summaries[stock.location_id].itemCount++;
          summaries[stock.location_id].totalUnits += stock.quantity_on_hand || 0;
          summaries[stock.location_id].totalValue +=
            (stock.quantity_on_hand || 0) * (stock.inventory_items?.unit_cost || 0);

          if (stock.quantity_on_hand <= (stock.inventory_items?.reorder_point || 0)) {
            summaries[stock.location_id].lowStockCount++;
          }
        });

        setStockSummaries(summaries);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocationIcon = (type) => {
    return locationTypeIcons[type] || MapPin;
  };

  const getLocationColor = (type) => {
    return locationTypeColors[type] || colors.accent;
  };

  // Group locations by type
  const groupedLocations = locations.reduce((acc, loc) => {
    const type = loc.location_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(loc);
    return acc;
  }, {});

  const typeLabels = {
    warehouse: 'Warehouses',
    office: 'Offices',
    truck: 'Trucks'
  };

  // Calculate total stats
  const totalStats = Object.values(stockSummaries).reduce((acc, summary) => ({
    itemCount: acc.itemCount + summary.itemCount,
    totalUnits: acc.totalUnits + summary.totalUnits,
    totalValue: acc.totalValue + summary.totalValue,
    lowStockCount: acc.lowStockCount + summary.lowStockCount
  }), { itemCount: 0, totalUnits: 0, totalValue: 0, lowStockCount: 0 });

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem',
        color: colors.textMuted
      }}>
        <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: '0.75rem' }}>Loading locations...</span>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        color: colors.textMuted
      }}>
        <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
        <p style={{ margin: 0 }}>No locations configured</p>
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
          Enable multi-location mode in Settings to add locations
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Total Summary */}
      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        padding: '1.25rem',
        marginBottom: '1.5rem',
        border: `1px solid ${colors.border}`
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          gap: '1rem'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: colors.accent, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {locations.length}
            </div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Locations</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: colors.text, fontSize: '1.5rem', fontWeight: 'bold' }}>
              {totalStats.itemCount}
            </div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Unique Items</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              color: totalStats.lowStockCount > 0 ? colors.warning : colors.text,
              fontSize: '1.5rem',
              fontWeight: 'bold'
            }}>
              {totalStats.lowStockCount}
            </div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Low Stock</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: colors.text, fontSize: '1.5rem', fontWeight: 'bold' }}>
              ${totalStats.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
            <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Total Value</div>
          </div>
        </div>
      </div>

      {/* Locations by Type */}
      {Object.entries(groupedLocations).map(([type, locs]) => (
        <div key={type} style={{ marginBottom: '1.5rem' }}>
          <h3 style={{
            margin: '0 0 1rem 0',
            color: colors.text,
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            {(() => {
              const Icon = getLocationIcon(type);
              return <Icon size={20} color={getLocationColor(type)} />;
            })()}
            {typeLabels[type] || 'Other'}
            <span style={{
              fontSize: '0.85rem',
              color: colors.textMuted,
              fontWeight: 'normal'
            }}>
              ({locs.length})
            </span>
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {locs.map(location => {
              const Icon = getLocationIcon(location.location_type);
              const typeColor = getLocationColor(location.location_type);
              const summary = stockSummaries[location.id] || {
                itemCount: 0,
                totalUnits: 0,
                totalValue: 0,
                lowStockCount: 0
              };

              return (
                <div
                  key={location.id}
                  style={{
                    background: colors.bgCard,
                    borderRadius: '12px',
                    border: location.is_default
                      ? `2px solid ${colors.accent}`
                      : `1px solid ${colors.border}`,
                    overflow: 'hidden',
                    transition: 'transform 0.2s, box-shadow 0.2s'
                  }}
                >
                  {/* Card Header */}
                  <div style={{
                    background: `${typeColor}15`,
                    padding: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    borderBottom: `1px solid ${colors.border}`
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: `${typeColor}25`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon size={22} color={typeColor} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        color: colors.text,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        {location.name}
                        {location.is_default && (
                          <Star size={14} color={colors.accent} fill={colors.accent} />
                        )}
                      </div>
                      {location.address && (
                        <div style={{
                          color: colors.textMuted,
                          fontSize: '0.8rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '180px'
                        }}>
                          {location.address}
                        </div>
                      )}
                    </div>
                    {summary.lowStockCount > 0 && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        background: `${colors.warning}20`,
                        borderRadius: '4px',
                        color: colors.warning,
                        fontSize: '0.75rem',
                        fontWeight: 'bold'
                      }}>
                        <AlertTriangle size={12} />
                        {summary.lowStockCount}
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.75rem'
                  }}>
                    <div style={{
                      background: colors.bgInput,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        color: colors.text,
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem'
                      }}>
                        <Package size={16} color={colors.accent} />
                        {summary.itemCount}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                        Items
                      </div>
                    </div>
                    <div style={{
                      background: colors.bgInput,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        color: colors.text,
                        fontWeight: 'bold',
                        fontSize: '1.25rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.25rem'
                      }}>
                        <DollarSign size={16} color={colors.accent} />
                        {summary.totalValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.75rem' }}>
                        Value
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{
                    padding: '0 1rem 1rem',
                    display: 'flex',
                    gap: '0.5rem'
                  }}>
                    <button
                      onClick={() => onViewLocation && onViewLocation(location.id)}
                      style={{
                        flex: 1,
                        padding: '0.6rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text,
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.35rem'
                      }}
                    >
                      <Eye size={14} /> View
                    </button>
                    <button
                      onClick={() => onTransfer && onTransfer(location.id)}
                      style={{
                        flex: 1,
                        padding: '0.6rem',
                        background: colors.bgInput,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.accent,
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '0.35rem'
                      }}
                    >
                      <ArrowRight size={14} /> Transfer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
